import { createAsyncThunk } from "@reduxjs/toolkit";
import { useReducer } from "react";
import {
    setSelectedOrganisation,
    setSelectedProject,
    setUpdatedOrganisation,
} from "reducers/commonSlice";
import { apiAgent } from "../config/axiosConfig";
import {
    cacheEntity,
    setFlatCollectionEntities,
    setOwners,
    setResourceSummary,
    setSidebarState,
    updateCollectionEntities,
    updateCollections,
    updateCurrentUser,
    updateEntity as updateEntityAction,
    updateEntityResponse,
    updateFolderEntities,
    updateFolders,
    updateLoadError,
    updateLoadingStage,
    updateState,
} from "./currentUserSlice";

export const Signup = createAsyncThunk(
  "signupuser",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/users",
        params: {
          user: {
            email: body.email,
            name: body.name,
            password: body.password,
            password_confirmation: body.password,
            token: body.token,
          },
        },
      });
      if (res.status === 201) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      let timedout =
        err.code === "ECONNABORTED" || err.response?.status === 500;
      dispatch(
        updateLoadError({ error_message: err.response.data.errors, timedout })
      );
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);

export const GoogleSignup = createAsyncThunk(
  "signupuser",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/google_signup",
        params: {
          data: body.credential,
          invitation_token: body.invitationToken,
        },
      });
      if (res.status === 200) {
        localStorage.setItem("token", res.data.jwt);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        await dispatch(updateCurrentUser(res.data.user));

        return res.data;
      }
    } catch (err) {
      let timedout =
        err.code === "ECONNABORTED" || err.response?.status === 500;
      dispatch(
        updateLoadError({ error_message: err.response.data.errors, timedout })
      );
    }
  }
);

export const Login = createAsyncThunk(
  "loginuser",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/users/sign_in",
        params: {
          email: body.email,
          password: body.password,
        },
      });

      if (res.status === 200) {
        localStorage.setItem("token", res.data.jwt);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        dispatch(updateCurrentUser(res.data.user));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
      return err.response.data.message;
    }
  }
);

export const confirmEmail = createAsyncThunk(
  "confirmEmail",
  async (token, { rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/users/confirm_email",
        params: { token },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return rejectWithValue(
        err.response ? err.response.data : { error: err.message }
      );
    }
  }
);

export const inviteByEmail = createAsyncThunk(
  "inviteByEmail",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: `/api/v1/projects/${body.projectId}/invite_by_email`,
        params: {
          email: body.email,
          role: body.role,
          name: body.name,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response.data;
    }
  }
);

export const resendConfirmationEmail = createAsyncThunk(
  "resendConfirmationEmail",
  async (email, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/users/resend_email_confirmation",
        params: {
          email: email,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        const [state, setState] = useReducer(reducer, {
          errors: {},
        });

        setState({
          type: "SET_STATE",
          payload: {
            isLoading: false,
            resendError: res.data || "Failed to resend confirmation email",
          },
        });

        return rejectWithValue(res.data);
      }
    } catch (err) {
      let timedout =
        err.code === "ECONNABORTED" || err.response?.status === 500;
      dispatch(
        updateLoadError({ error_message: err.response.data.errors, timedout })
      );
    }
  }
);

export const createOrganisation = createAsyncThunk(
  "createOrganisation",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/organisations",
        params: {
          organisation: {
            name: body.companyName,
            url: body.companyUrl,
            location: body.companyLocation,
          },
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const sendApiCall = createAsyncThunk(
  "sendApiCall",
  async ({ id, selectedEnvironmentId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "get",
        url: `/api/v1/entities/${id}/run`,
        params: {
          environment_set_id: selectedEnvironmentId,
        },
      });

      if (res.status === 200) {
        await dispatch(
          updateEntityResponse({
            id: res.data?.response?.entity_id,
            response: res.data?.response,
          })
        );
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const createCollection = createAsyncThunk(
  "createCollection",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: `/api/v1/projects/${body.id}/collections`,
        params: {
          collection: {
            name: "New Collection",
          },
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const destroyCollection = createAsyncThunk(
  "destroyCollection",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "delete",
        url: `/api/v1/collections/${body.id}`,
        params: {},
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

// TODO :: Complete the following thunk action once backend is ready
export const generateResourceOverview = createAsyncThunk(
  "generateResourceOverview",
  async (resource, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "POST",
        url: `/api/v1/${resource.type}/${resource.id}/generate_docs`,
      });
      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const destroyFolder = createAsyncThunk(
  "destroyFolder",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "delete",
        url: `/api/v1/folders/${body.id}`,
        params: {},
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const destroyEntity = createAsyncThunk(
  "destroyEntity",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "delete",
        url: `/api/v1/entities/${body.id}`,
        params: {},
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const duplicateResource = createAsyncThunk(
  "duplicateResource",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: `/api/v1/${body.resource}/${body.resource_id}/duplicate`,
        params: {},
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const fetchSummary = createAsyncThunk(
  "fetchSummary",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "get",
        url: "/api/v1/summaries",
        params: {
          resource_id: body.id,
          resource_type: body.type,
        },
      });
      if (res.status === 200) {
        await dispatch(setResourceSummary(res.data.summary));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateResourceSummary = createAsyncThunk(
  "updateResourceSummary",
  async (body, { dispatch, rejectWithValue, history }) => {
    try {
      const res = await apiAgent({
        method: "patch",
        url: `/api/v1/summaries/${body.id}`,
        data: {
          summary: body?.summary,
        },
      });
      if (res.status === 200) {
        await dispatch(setResourceSummary(res.data.summary));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateCollection = createAsyncThunk(
  "updateCollection",
  async (body, { dispatch, rejectWithValue, history }) => {
    let collection = null;

    if (body?.authorization) {
      collection = {
        authorization: body?.authorization,
      };
    } else if (body?.variables) {
      collection = {
        variables: body?.variables,
      };
    } else if (body?.name) {
      collection = {
        name: body?.name,
      };
    } else if (body?.settings) {
      collection = {
        allowed_hosts: body?.settings?.allowedHosts,
        blocked_paths: body?.settings?.blockedPaths,
        pii_masking: body?.settings?.piiMaskingKeys,
        auto_documentation: body?.settings?.autoDocumentation,
        enable_monitoring: body?.settings?.enableMonitoring,
      };
    } else {
      collection = {
        overview: body?.overview,
      };
    }
    try {
      const res = await apiAgent({
        method: "patch",
        url: `/api/v1/collections/${body.id}`,
        data: {
          collection: collection,
        },
      });
      if (res.status === 200) {
        await dispatch(fetchProjectCollections(res.data.collection.project_id));
        await dispatch(updateState(res.data));
        await dispatch(setSidebarState(res.data.collection));

        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateEntity = createAsyncThunk(
  "updateEntity",
  async (body, { dispatch, rejectWithValue, history }) => {
    let entity = {};

    if (body?.name) {
      entity = {
        name: body?.name,
      };
    } else if (body?.entity) {
      entity = body?.entity;
    } else if (body?.testType) {
      entity = { test_type: body?.testType };
    } else {
      entity = {
        enable_variables: body?.enable_variables,
      };
    }
    try {
      const res = await apiAgent({
        method: "patch",
        url: `/api/v1/entities/${body.id}`,
        data: {
          entity: entity,
        },
      });
      if (res.status === 200) {
        res.data.collection = body.collection;
        res.data.folder = body.folder;
        if (body.isCollectionApi) {
          res.data.collectionApi = res.data.entity;
        } else {
          res.data.folderApi = res.data.entity;
        }
        await dispatch(updateState(res.data));
        await dispatch(setSidebarState(res.data.entity));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateFolder = createAsyncThunk(
  "updateFolder",
  async (body, { dispatch, rejectWithValue }) => {
    let folder = {};

    if (body?.authorization) {
      folder = {
        authorization: body?.authorization,
      };
    } else if (body?.name) {
      folder = {
        name: body?.name,
      };
    }
    try {
      const res = await apiAgent({
        method: "patch",
        url: `/api/v1/folders/${body.id}`,
        data: {
          folder: folder,
        },
      });
      if (res.status === 200) {
        await dispatch(fetchCollectionFolders(res.data.folder.collection_id));

        res.data.collection = body.collection;
        await dispatch(updateState(res.data));
        await dispatch(setSidebarState(res.data.folder));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const createFolder = createAsyncThunk(
  "createFolder",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: `/api/v1/collections/${body.id}/folders`,
        data: {
          folder: {
            name: "New Folder",
          },
        },
      });
      return res.data;
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const createProject = createAsyncThunk(
  "createProject",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: `/api/v1/organisations/${body.organisationId}/projects`,
        params: {
          name: body.projectName,
        },
      });

      if (res.status === 200) {
        localStorage.setItem("project", JSON.stringify(res.data));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return rejectWithValue(err?.payload?.response);
      // dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const getOrganisations = createAsyncThunk(
  "getOrganisations",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "get",
        url: "/api/v1/organisations",
      });

      if (res.status === 200) {
        localStorage.setItem("project", JSON.stringify(res.data.project));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const getPastResponses = createAsyncThunk(
  "getPastResponses",
  async ({ entityId, page, perPage }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "get",
        url: `/api/v1/responses`,
        params: {
          entity_id: entityId,
          page: page,
          per_page: perPage,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const fetchProjectMembers = createAsyncThunk(
  "fetchProjectMembers",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const projectId = body;
      const res = await apiAgent({
        method: "get",
        url: `/api/v1/projects/${projectId}/members`,
      });
      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const deleteProjectMember = createAsyncThunk(
  "deleteProjectMember",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "delete",
        url: `/api/v1/projects/${_.projectId}/delete_member?member_id=${_.memberId}`,
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchProjectCollections = createAsyncThunk(
  "fetchProjectCollections",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const projectId = body;
      const res = await apiAgent({
        method: "get",
        url: `/api/v1/projects/${projectId}/collections`,
      });
      if (res.status === 200) {
        await dispatch(updateCollections(res.data.collections));
        return res;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const fetchCollectionFolders = createAsyncThunk(
  "fetchCollectionFolders",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const collectionId = body;
      const res = await apiAgent({
        method: "get",
        url: `/api/v1/collections/${collectionId}/folders`,
      });
      if (res.status === 200) {
        await dispatch(updateFolders({ data: res.data, collectionId }));

        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const fetchCollectionApis = createAsyncThunk(
  "fetchCollectionApis",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const collectionId = body;

      const res = await apiAgent({
        method: "get",
        url: "/api/v1/entities",
        params: {
          collection_id: collectionId,
        },
      });

      if (res.status === 200) {
        await dispatch(
          updateCollectionEntities({ data: res.data, collectionId })
        );
        return res;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const fetchFoldersApis = createAsyncThunk(
  "fetchFoldersApis",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const folderIds = body;

      return Promise.all(
        folderIds.map((folderId) =>
          apiAgent({
            method: "get",
            url: `/api/v1/entities?folder_id=${folderId}`,
          })
        )
      )
        .then((response) => {
          const updateFolderApis = async (folderId, entities) => {
            await dispatch(updateFolderEntities({ folderId, entities }));
          };
          folderIds.map((folderId, index) => {
            updateFolderApis(folderId, response[index]?.data?.entities || []);
          });
        })
        .catch((error) => {
          dispatch(updateLoadError({ error_message: error.message }));
        });
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateRequest = createAsyncThunk(
  "updateRequest",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "patch",
        url: `/api/v1/request/${body?.id}`,
        data: {
          request: body?.request,
          use_local_agent: body?.use_local_agent,
        },
      });
      if (res.status === 200) {
        await dispatch(
          updateEntityAction({ id: res.data?.entity_id, request: res.data })
        );
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const getEntity = createAsyncThunk(
  "getEntity",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const resource = body;

      const res = await apiAgent({
        method: "get",
        url: `/api/v1/entities/${body.id}`,
      });
      if (res.status === 200) {
        await dispatch(cacheEntity({ id: body.id, data: res.data }));
        // TODO :: Update local state apis record when any entity is updated
        // await dispatch(updateCollectionApi({ id: body.id, data: res.data }));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const fetchEntityRequest = createAsyncThunk(
  "fetchEntityRequest",
  async (entityId, { dispatch, rejectWithValue, getState }) => {
    try {
      const res = await apiAgent({
        method: "get",
        url: "/api/v1/request",
        params: { entity_id: entityId },
      });

      if (res) {
        const state = getState();
        const existingTab = state.tabs?.tabs.find((tab) => tab.id === entityId);
        const updatedTab = {
          ...existingTab,
          request: {
            ...res.data, // Merge new data
          },
        };
        await dispatch(cacheEntity({ id: entityId, data: updatedTab }));
        return res.data;
      } else {
        return rejectWithValue("No response data received.");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "An error occurred.";
      dispatch(updateLoadError({ error_message: errorMessage }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const createEntity = createAsyncThunk(
  "createEntity",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/entities",
        data: {
          entity: {
            folder_id: body.folder?.id,
            collection_id: body.collection?.id,
            name: "New Request",
          },
        },
      });
      if (res.status === 200) {
        return { entity: res.data };
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      // let timedout = err.code === 'ECONNABORTED' || err.response?.status === 500;
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "updateUserProfile",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "patch",
        url: `/api/v1/users/${body.id}`,
        data: {
          user: {
            name: body.name,
            // username: body.username,
            // email: body.email,
            notification_settings: body.notificationSettings,
          },
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const fetchFlatCollectionEntities = createAsyncThunk(
  "fetchFlatCollectionEntities",
  async (collectionId, { dispatch, rejectWithValue }) => {
    await dispatch(updateLoadingStage(true));
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/entities/flat?collection_id=${collectionId}`,
      });
      if (response.status === 200) {
        await dispatch(
          setFlatCollectionEntities({
            entities: response?.data?.entities,
            collectionId: collectionId,
          })
        );
        await dispatch(updateLoadingStage(false));
        return response?.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const getOwners = createAsyncThunk(
  "getOwners",
  async ({ projectId }, { dispatch }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/projects/${projectId}/users`,
      });
      if (response.status === 200) {
        await dispatch(setOwners(response.data.users));
        return response.data;
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateOrganisation = createAsyncThunk(
  "updateOrganisation",
  async (body, { dispatch, rejectWithValue }) => {
    const data = {};
    if (body.name) data.name = body.name;
    if (body.host) data.host = body.host;
    if (body.location) data.location = body.location;
    try {
      const res = await apiAgent({
        method: "PUT",
        url: `/api/v1/organisations/${body.id}`,
        data: data,
      });

      if (res.status === 200) {
        await dispatch(setUpdatedOrganisation(res.data.organisation));
        await dispatch(setSelectedOrganisation(res.data.organisation));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const getOrganisation = createAsyncThunk(
  "getOrganisation",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "GET",
        url: `/api/v1/organisations/${body.id}`,
      });

      if (res.status === 200) {
        await dispatch(setUpdatedOrganisation(res.data));
        await dispatch(setSelectedOrganisation(res.data));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const updateProject = createAsyncThunk(
  "updateProject",
  async ({ id, ...body }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "PUT",
        url: `/api/v1/projects/${id}`,
        data: body,
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return rejectWithValue(err?.payload?.response);
      // dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const getProject = createAsyncThunk(
  "getProject",
  async ({ projectId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "GET",
        url: `/api/v1/projects/${projectId}`,
      });

      if (res.status === 200) {
        dispatch(setSelectedProject(res.data));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const invalidateCache = createAsyncThunk(
  "invalidateCache",
  async ({ orgId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "POST",
        url: `/api/v1/organisations/${orgId}/invalidate`,
      });
      if (res.status === 200) {
        return res.data;
      }
    } catch (err) {
      return err.response.data;
    }
  }
);

export const updateUserRoleForProject = createAsyncThunk(
  "updateUserrRoleForProject",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "put",
        url: `/api/v1/invitations/${body.memberId}`,
        params: {
          role: body.role,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
      return err.response.data.message;
    }
  }
);

export const sendPasswordResetInstructions = createAsyncThunk(
  "sendPasswordResetInstructions",
  async (email, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/passwords/send_instructions",
        params: {
          email: email,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(
        updateLoadError({
          error_message:
            err.response?.data?.message || "Failed to send reset instructions",
        })
      );
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "resetPassword",
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/passwords/reset",
        params: {
          reset_password_token: body.reset_password_token,
          password: body.password,
          confirm_password: body.confirm_password,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(
        updateLoadError({
          error_message:
            err.response?.data?.message || "Failed to reset password",
        })
      );
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const autoLogin = createAsyncThunk(
  "user/autoLogin",
  async (token, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/users/auto_login",
        params: {
          token: token,
        },
      });

      if (res.status === 200) {
        localStorage.setItem("token", res.data.jwt);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        dispatch(updateCurrentUser(res.data.user));
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      dispatch(
        updateLoadError({
          error_message: err.response?.data?.message || "Auto login failed",
        })
      );
      return rejectWithValue(err.response?.data || "Auto login failed");
    }
  }
);

export const authenticateSupabase = createAsyncThunk(
  "user/authenticateSupabase",
  async ({ code, projectId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "get",
        url: "/api/v1/auth/supabase_callback",
        params: {
          project_id: projectId,
          code: code,
          origin_host: window.location.origin,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const authenticateGithub = createAsyncThunk(
  "user/authenticateGithub",
  async ({ code, projectId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "get",
        url: "/api/v1/auth/github_callback",
        params: {
          project_id: projectId,
          code: code,
          origin_host: window.location.origin,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const submitSelectedGithubRepos = createAsyncThunk(
  "user/submitSelectedGithubRepos",
  async ({ selectedRepos, projectId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "post",
        url: "/api/v1/github/import_repos",
        data: {
          project_id: projectId,
          selected_repos: selectedRepos,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const regenerateApiDoc = createAsyncThunk(
  "regenerateApiDoc",
  async ({ entityId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "POST",
        url: `/api/v1/entities/${entityId}/regenerate_api_doc`,
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const deprecateEntity = createAsyncThunk(
  "deprecateEntity",
  async ({ entityId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "POST",
        url: `/api/v1/entities/${entityId}/deprecate_api`,
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const editEntity = createAsyncThunk(
  "editEntity",
  async ({ entityId, updateData }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "PUT",
        url: `/api/v1/entities/${entityId}`,
        data: {
          entity: {
            ...updateData,
          },
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const getDatabaseConfig = createAsyncThunk(
  "getDatabaseConfig",
  async ({ projectId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "GET",
        url: `/api/v1/database_configs`,
        params: {
          project_id: projectId,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const createDatabaseConfig = createAsyncThunk(
  "createDatabaseConfig",
  async (
    { projectId, dbType, host, name, port, username, password },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const res = await apiAgent({
        method: "POST",
        url: `/api/v1/database_configs/`,
        data: {
          project_id: projectId,
          db_type: dbType,
          host: host,
          name: name,
          port: port,
          username: username,
          password: password,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const updateDatabaseConfig = createAsyncThunk(
  "updateDatabaseConfig",
  async (
    { databaseConfigId, dbType, host, name, port, username, password },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const res = await apiAgent({
        method: "PUT",
        url: `/api/v1/database_configs/${databaseConfigId}`,
        data: {
          db_type: dbType,
          host: host,
          name: name,
          port: port,
          username: username,
          password: password,
        },
      });

      if (res.status === 200) {
        return res.data;
      } else {
        return rejectWithValue(res.data);
      }
    } catch (err) {
      return err.response?.data;
    }
  }
);

export const updateEntityLocalAgentResponse = createAsyncThunk(
  "updateEntityLocalAgentResponse",
  async ({ entityId, updateData }, { dispatch, rejectWithValue }) => {
    const data = {
      response_body: updateData.body,
      response_code: updateData.status,
      response_headers: updateData.headers,
      request_time: updateData.request_time,
      request_type: updateData?.request_json?.request_type,
      url: updateData?.request_json?.url,
      params: updateData?.request_json?.params,
      headers: updateData?.request_json?.headers,
      body: updateData?.request_json?.body,
    };
    try {
      const response = await apiAgent({
        method: "POST",
        url: `/api/v1/entities/${entityId}/update_response`,
        data: {
          ...data,
        },
      });
      if (response.status === 200) {
        await dispatch(
          updateEntityResponse({
            id: response.data?.response?.entity_id,
            response: response.data?.response,
          })
        );
        return response?.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      dispatch(updateLoadError({ error_message: err.response.data.message }));
    }
  }
);

export const getUserDetails = createAsyncThunk(
  "getUserDetails",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiAgent({
        method: "GET",
        url: `/api/v1/users/me`,
      });

      const user = res?.data?.user;

      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        await dispatch(updateCurrentUser(user));
        return user;
      } else {
        const errorMessage = res?.data?.message || "User data not found";
        return rejectWithValue({ message: errorMessage });
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Unknown error occurred";
      dispatch(updateLoadError({ error_message: errorMessage }));
    }
  }
);

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_STATE": {
      return {
        ...state,
        ...action.payload,
      };
    }
  }
};
